import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { WatchComment } from "@/hooks/use-watch";
import type { Shape } from "./annotation-context";
import { ShapeElement } from "./annotation-canvas";

// Frame-exact visibility window. Annotation renders only when the playhead
// is essentially AT the comment's timecode — not lingering before or after.
// ±0.05s (50ms) tolerance is tight enough that annotations don't spill into
// neighboring content, but loose enough to survive float precision when the
// user clicks a comment card to seek exactly to its timecode.
//
// Why not 0? currentTime after seek can drift by a millisecond or two before
// the browser settles; equality-only would flicker.
const VISIBLE_WINDOW_BEFORE = 0.05;
const VISIBLE_WINDOW_AFTER  = 0.05;

type Props = {
  /** Master toggle — Settings → View on Asset → Annotations. */
  visible: boolean;
  /** Stack-wide comment list. Filtered to those with annotationsUrl. */
  comments: WatchComment[];
  /** Current playback time in seconds. Drives which comments' shapes render. */
  currentTime: number;
};

type BlobBody = { version?: number; shapes?: unknown[] };

/**
 * Read-only overlay for existing comment annotations.
 *
 * When `visible` is true, fetches each comment's annotation blob from R2 and
 * renders shapes whose parent comment's timecode is within the visible window
 * around `currentTime`. Comments with no timecode (image asset comments,
 * general notes) render whenever the toggle is on.
 *
 * Why this exists: the composer's `AnnotationCanvas` renders ONLY draft shapes
 * from React state. Saved annotations from previously-posted comments have to
 * come from R2 — this component is the read-side of that flow.
 *
 * Rendering matches AnnotationCanvas exactly (same SVG, same `ShapeElement`),
 * so a shape looks identical whether it's being drawn now or replayed later.
 *
 * pointer-events-none — never blocks click-to-play on the video.
 */
export function SavedAnnotationsOverlay({ visible, comments, currentTime }: Props) {
  // Stable list of {commentId, url, timestampSeconds} for annotated comments.
  // Recomputed only when comments identity changes, so useQueries doesn't
  // churn queries every timeupdate tick.
  const annotated = useMemo(
    () =>
      comments
        .filter((c) => !!c.annotationsUrl)
        .map((c) => ({
          id: c.id,
          url: c.annotationsUrl as string,
          timestampSeconds: c.timestampSeconds,
        })),
    [comments],
  );

  // One query per annotation blob. staleTime=Infinity because blobs are
  // immutable — a comment's annotations never change post-write. If a blob
  // fails (404, CORS, etc.) we silently skip it rather than blocking video.
  const queries = useQueries({
    queries: annotated.map((a) => ({
      queryKey: ["annotation-blob", a.url],
      queryFn: async (): Promise<Shape[]> => {
        const res = await fetch(a.url);
        if (!res.ok) throw new Error(`blob ${res.status}`);
        const body = (await res.json()) as BlobBody;
        if (!body || !Array.isArray(body.shapes)) return [];
        // Filter to shape types we can render — silently drop legacy pin/text.
        return body.shapes.filter(isRenderableShape);
      },
      staleTime: Infinity,
      retry: false,
      // Only fetch when the overlay is actually visible — no traffic while off.
      enabled: visible,
    })),
  });

  if (!visible) return null;

  // Zip shapes back to their comment + apply timecode window.
  const visibleShapes: Shape[] = [];
  for (let i = 0; i < annotated.length; i++) {
    const a = annotated[i];
    const q = queries[i];
    if (!q?.data?.length) continue;
    // Comments with no timestamp (image asset or general note) always show
    // while the toggle is on.
    if (a.timestampSeconds == null) {
      visibleShapes.push(...q.data);
      continue;
    }
    const t = a.timestampSeconds;
    if (
      currentTime >= t - VISIBLE_WINDOW_BEFORE &&
      currentTime <= t + VISIBLE_WINDOW_AFTER
    ) {
      visibleShapes.push(...q.data);
    }
  }

  if (visibleShapes.length === 0) return null;

  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {visibleShapes.map((s, i) => (
        <ShapeElement key={i} shape={s} />
      ))}
    </svg>
  );
}

// Cheap runtime shape-type check. Server allowlist includes pin + text; we
// don't render either (pin is handled by the separate anchor-pin overlay,
// text was never wired up in the composer). Silently drop them here.
function isRenderableShape(v: unknown): v is Shape {
  if (!v || typeof v !== "object") return false;
  const t = (v as { type?: unknown }).type;
  return t === "arrow" || t === "line" || t === "box" || t === "draw";
}
